import { CodeType, isSorCode } from '../../../../entities/shared/code.type';
import { TaskLinkType } from '../../../../entities/tasks/task.enum';
import { JobSection, JobSectionRightIds, JobSectionVerb } from '../../../modules/job.section.rights';
import { JobSecurityRight } from '../../../modules/job.security.module';
import { TaskSecurityRight } from '../../../modules/task.security.module';
import { SecurityRoleTiny } from '../../../security.role';
import { SecurityRoleUtility } from '../../../utility/security.roles.utility';
import { WorkOrder } from '../../../../entities/job/workorder';
import { stageIsClosedOrCancelled, stageIsFinalised } from '../stage/stage.security.state';

export type SectionRights = Record<JobSectionVerb, boolean>;
export type JobSectionRights = Record<JobSection, SectionRights>;
export type SectionWriteVerb = Exclude<JobSectionVerb, 'view'>;
export type LineSection = Exclude<JobSection, 'tasks'>;
type ExportableLine = { type?: CodeType; active?: boolean };

/** Parents whose tasks sit on a web job screen; Opportunity and Job Template tasks follow their own rights. */
const JOB_TASK_PARENTS: TaskLinkType[] = [TaskLinkType.Job, TaskLinkType.WorkOrder, TaskLinkType.Work];

const has = (role: SecurityRoleTiny, right: number) => SecurityRoleUtility.checkTinyRights(role, right);

/** The one reader of rights 1031–1042; the web screens and the server both decide from it. */
export function getJobSectionRights(role: SecurityRoleTiny): JobSectionRights {

    const section = (ids: Record<JobSectionVerb, number>): SectionRights => ({
        view: has(role, ids.view),
        create: has(role, ids.create),
        edit: has(role, ids.edit),
        delete: has(role, ids.delete),
    });

    return {
        tasks: section(JobSectionRightIds.tasks),
        items: section(JobSectionRightIds.items),
        sors: section(JobSectionRightIds.sors),
    };
}

export function itemLineSection(line: { type?: CodeType }): LineSection {

    return isSorCode(line) ? 'sors' : 'items';
}

/** An export writes only the line kinds its screen lists, so a file never shows what the screen hides (WBS-11929). */
export function exportLines<T extends { type?: CodeType }>(lines: T[] | null | undefined, sections: Partial<Record<JobSection, boolean>>): T[] {

    return (lines ?? []).filter(line => !!sections[itemLineSection(line)]);
}

/** The job ⋮ export's refusal, read when the role can see neither section. */
export const JOB_EXPORT_REFUSAL = 'Your role can\'t see Items or SORs.';

/**
 * The job ⋮ export holds the line kinds of the tabs the user can see: an Items tab needs Items › View (every
 * Items mode shows one), the SOR tab needs SORs on and SORs › View (WBS-11929).
 */
export function jobExportSections(role: SecurityRoleTiny | null | undefined, sorsOn: boolean): Record<'items' | 'sors', boolean> {

    return {
        items: !role || canViewSection(role, 'items'),
        sors: sorsOn && (!role || canViewSection(role, 'sors')),
    };
}

/**
 * A New Job line tab needs Jobs Manage only: Items and SORs rights govern lines on an existing job, and creating a
 * job never reads them, on any path (WBS-12277, WBS-12547).
 */
export function newJobLineTabs(role: SecurityRoleTiny, sorsOn: boolean): Record<LineSection, boolean> {

    const manage = has(role, JobSecurityRight.Manage);

    return {
        items: manage,
        sors: sorsOn && manage,
    };
}

/** Access reads the role only; the SOR switches decide what the file holds, not who may export. */
export function canExportJobItems(role: SecurityRoleTiny | null | undefined): boolean {

    return !role || canViewSection(role, 'items') || canViewSection(role, 'sors');
}

/**
 * The lines a stage tab lists: active approved and active pending (requested) lines — the legacy Items tab's export set.
 * Pending lines are marked by the array they come from: the stored `isClaimCode` is missing on lines older than the 2025.1
 * data repair, and the file's Item Status reads a pending line's own status name (WBS-11893).
 */
export function stageExportLines<T extends ExportableLine, P extends ExportableLine>(stage: { codes?: T[]; claimCodes?: P[] } | null | undefined, sections: Partial<Record<JobSection, boolean>>): (T | P)[] {

    const pending = (stage?.claimCodes ?? []).map(line => ({ ...line, isClaimCode: true }));

    return exportLines<T | P>([...(stage?.codes ?? []), ...pending].filter(line => line.active !== false), sections);
}

export function canViewSection(role: SecurityRoleTiny, section: JobSection): boolean {

    return has(role, JobSectionRightIds[section].view);
}

/** The section right AND Jobs Manage, which every item write needed before the section rights existed. */
export function canWriteItemLine(role: SecurityRoleTiny, line: { type?: CodeType }, verb: SectionWriteVerb): boolean {

    return has(role, JobSecurityRight.Manage) && has(role, JobSectionRightIds[itemLineSection(line)][verb]);
}

/** The refusal a user reads when a section right is missing, for FE toasts and BE 403s alike. */
export function sectionRefusalMessage(section: JobSection, verb: JobSectionVerb): string {

    const names: Record<JobSection, string> = { tasks: 'tasks', items: 'items', sors: 'SORs' };
    const verbs: Record<JobSectionVerb, string> = { view: 'view', create: 'add', edit: 'edit', delete: 'delete' };

    return `You don't have permission to ${verbs[verb]} ${names[section]}.`;
}

export const JOBS_MANAGE_REFUSAL = 'You don\'t have permission to manage jobs.';

/** A SOR line is refused on every web add while either switch is off, as the job export already reads them (WBS-12107). */
export const SORS_OFF_REFUSAL = 'SORs are turned off for this account.';

export const STAGE_CLOSED_REFUSAL = 'The stage is closed or cancelled.';
export const STAGE_FINALISED_REFUSAL = 'The stage is finalised. Your role does not have Modify Job after Finalise.';

/** `canWriteItemLine` asks for Manage and the section right together; the refusal names whichever is missing, Manage first. */
export function itemWriteRefusalMessage(role: SecurityRoleTiny, line: { type?: CodeType }, verb: SectionWriteVerb): string {

    return has(role, JobSecurityRight.Manage) ? sectionRefusalMessage(itemLineSection(line), verb) : JOBS_MANAGE_REFUSAL;
}

export type JobImportCheck = {
    manage: boolean;
    sections: JobSectionRights;
    sorsOn: boolean;
    /** The existing stages the file adds lines to; new stages take any line. */
    stages: WorkOrder[];
    modifyAfterFinalise: boolean;
};

/** One SOR row refuses the whole file while SORs are off; the quote import reads no right, only this (WBS-12180). */
export function sorsOffImportRefusal(lines: { type?: CodeType }[], sorsOn: boolean): string | null {

    return !sorsOn && lines.some(line => isSorCode(line)) ? SORS_OFF_REFUSAL : null;
}

/**
 * The job ⋮ import sends one save per target, so a server refusal could keep part of a file. The screen checks
 * the whole file first with what it already knows and refuses it before any save (WBS-12107). The words are the
 * server's, so the user reads the same reason either way.
 */
export function jobImportRefusal(lines: { type?: CodeType }[], check: JobImportCheck): string | null {

    const sorsOff = sorsOffImportRefusal(lines, check.sorsOn);
    if (sorsOff) return sorsOff;
    if (!check.manage) return JOBS_MANAGE_REFUSAL;

    const refused = lines.find(line => !check.sections?.[itemLineSection(line)]?.create);
    if (refused) return sectionRefusalMessage(itemLineSection(refused), 'create');

    if (check.stages.some(stage => stageIsClosedOrCancelled(stage))) return STAGE_CLOSED_REFUSAL;
    if (!check.modifyAfterFinalise && check.stages.some(stage => stageIsFinalised(stage))) return STAGE_FINALISED_REFUSAL;

    return null;
}

/** New Job reads no Items or SORs right, so a file is refused only for a SOR line while SORs are off, or without Jobs Manage (WBS-12547). */
export function newJobImportRefusal(lines: { type?: CodeType }[], role: SecurityRoleTiny, sorsOn: boolean): string | null {

    const sorsOff = sorsOffImportRefusal(lines, sorsOn);
    if (sorsOff) return sorsOff;

    return has(role, JobSecurityRight.Manage) ? null : JOBS_MANAGE_REFUSAL;
}

/**
 * "Update pricing" re-prices every item line (SOR lines too) and rewrites draft invoices and bills, so it needs
 * Manage Invoices plus Edit on each section it rewrites.
 */
export function canUpdateJobPricing(role: SecurityRoleTiny, jobHasSorLines: boolean): boolean {

    return has(role, JobSecurityRight.ManageInvoices)
        && has(role, JobSectionRightIds.items.edit)
        && (!jobHasSorLines || has(role, JobSectionRightIds.sors.edit));
}

export function jobHasSorLines(job: { workOrders?: { codes?: { type?: CodeType }[]; claimCodes?: { type?: CodeType }[] }[] }): boolean {

    return (job?.workOrders ?? []).some(stage => [...(stage.codes ?? []), ...(stage.claimCodes ?? [])].some(line => isSorCode(line)));
}

export function isJobTask(task: { links?: { type?: string }[]; parent?: { type?: string } }): boolean {

    return [...(task?.links ?? []), ...(task?.parent ? [task.parent] : [])].some(link => JOB_TASK_PARENTS.includes(link.type as TaskLinkType));
}

export function isJobTaskParent(parentType?: string): boolean {

    return JOB_TASK_PARENTS.includes(parentType as TaskLinkType);
}

/**
 * The section right AND the task rights the job screens already asked for: Manage Manual Tasks for every
 * write, plus Delete Any, or Delete My on a task the user created.
 */
export function canWriteTask(role: SecurityRoleTiny, verb: SectionWriteVerb, task?: { createdBy?: { _id?: string } }, userId?: string): boolean {

    if (!has(role, JobSectionRightIds.tasks[verb]) || !has(role, TaskSecurityRight.ManageManualTasks)) return false;
    if (verb !== 'delete') return true;

    return has(role, TaskSecurityRight.DeleteAnyTasks) || (has(role, TaskSecurityRight.DeleteMyTasks) && !!userId && task?.createdBy?._id === userId);
}
